import api from './api'
import { getAllPage } from './pagination'

export interface Notification {
  id: number
  recipient: number
  recipient_name: string
  title: string
  message: string
  notification_type: string
  url?: string
  status: 'pending' | 'sent' | 'failed'
  created_at: string
  read_at?: string
}

export interface Message {
  id: number
  sender: number
  sender_name: string
  recipient: number
  recipient_name: string
  subject: string
  content: string
  message_type: 'email' | 'sms' | 'push' | 'in_app'
  status: string
  is_read?: boolean
  created_at: string
  read_at?: string | null
  reply_to?: number | null
}

export interface Announcement {
  id: number
  author: number
  author_name: string
  title: string
  content: string
  announcement_type: 'general' | 'academic' | 'financial' | 'event' | 'emergency'
  target_audience: 'all' | 'parents' | 'teachers' | 'students'
  priority: 'low' | 'normal' | 'high' | 'urgent'
  status: 'draft' | 'published' | 'archived'
  published_at?: string | null
  expires_at?: string | null
  /** Shown on the public sign-in page until it expires. */
  show_on_sign_in?: boolean
  created_at: string
  updated_at: string
}

export interface CommunicationRecipient {
  id: number
  name: string
  role: string
}

export const communicationService = {
  async getRecipients(): Promise<CommunicationRecipient[]> {
    const response = await api.get('/communication/recipients/')
    return response.data
  },
  // Notifications
  async getNotifications(params?: {
    page?: number
    page_size?: number
    notification_type?: string
    status?: string
  }): Promise<{ results: Notification[]; count: number }> {
    return getAllPage<Notification>('/communication/notifications/', params)
  },

  async getNotification(id: number): Promise<Notification> {
    const response = await api.get(`/communication/notifications/${id}/`)
    return response.data
  },

  async markNotificationAsRead(id: number): Promise<Notification> {
    const response = await api.patch(`/communication/notifications/${id}/`, {
      read_at: new Date().toISOString()
    })
    return response.data
  },

  async markAllNotificationsAsRead(): Promise<void> {
    await api.post('/communication/notifications/mark-all-read/')
  },

  // Messages
  async getMessages(params?: {
    page?: number
    page_size?: number
    message_type?: string
    status?: string
  }): Promise<{ results: Message[]; count: number }> {
    return getAllPage<Message>('/communication/messages/', params)
  },

  async getMessage(id: number): Promise<Message> {
    const response = await api.get(`/communication/messages/${id}/`)
    return response.data
  },

  async sendMessage(messageData: {
    recipient: number
    subject: string
    content: string
    message_type?: string
    reply_to?: number
  }): Promise<Message> {
    const response = await api.post('/communication/messages/', messageData)
    return response.data
  },

  async markMessageAsRead(id: number): Promise<Message> {
    const response = await api.patch(`/communication/messages/${id}/`, {
      read_at: new Date().toISOString()
    })
    return response.data
  },

  async markAllMessagesAsRead(): Promise<void> {
    await api.post('/communication/messages/mark-all-read/')
  },

  // Announcements
  async getAnnouncements(params?: {
    page?: number
    page_size?: number
    target_audience?: string
    priority?: string
  }): Promise<{ results: Announcement[]; count: number }> {
    return getAllPage<Announcement>('/communication/announcements/', params)
  },

  async getAnnouncement(id: number): Promise<Announcement> {
    const response = await api.get(`/communication/announcements/${id}/`)
    return response.data
  },

  async createAnnouncement(announcementData: {
    title: string
    content: string
    target_audience?: string
    priority?: string
    announcement_type?: string
    expires_at?: string | null
    show_on_sign_in?: boolean
  }): Promise<Announcement> {
    const response = await api.post('/communication/announcements/', announcementData)
    return response.data
  },

  async setAnnouncementOnSignIn({ id, show }: { id: number; show: boolean }): Promise<Announcement> {
    const response = await api.patch(`/communication/announcements/${id}/`, { show_on_sign_in: show })
    return response.data
  },

  // FCM Token
  async updateFCMToken(token: string): Promise<void> {
    await api.post('/communication/fcm-token/', {
      fcm_token: token
    })
  },

  // Unread counts
  async getUnreadCounts(): Promise<{
    unread_messages: number
    unread_notifications: number
  }> {
    const response = await api.get('/communication/unread-counts/')
    return response.data
  },
}
